/**
 * enLandingScenes — the demo chat content of the landing page's animated
 * product scenes (and of the MP4s rendered from them by tools/video).
 *
 * Deliberately dependency-free (a plain object, no imports): the Remotion
 * project in tools/video imports this file by relative path so the videos and
 * the web scenes can never drift. `scenes/sceneScripts.ts` references these
 * keys; the web renders them through t(), the video looks them up here.
 *
 * The Hebrew lines are demo CONTENT — they show a customer writing in Hebrew
 * and the flow answering in kind. They are not a second UI locale.
 */
export const enLandingScenes = {
  // ── Shared chrome ────────────────────────────────────────────────────────
  'landing.scene.channel.instagram': 'Instagram',
  'landing.scene.channel.whatsapp': 'WhatsApp',
  'landing.scene.channel.messenger': 'Messenger',
  'landing.scene.channel.email': 'Email',
  'landing.scene.brandName': 'Glow Studio',
  'landing.scene.autoTag': 'Automated',
  'landing.scene.typing': 'typing…',
  'landing.scene.sampleData': 'Illustrative sample data',
  'landing.scene.dmHeader': 'Direct message',
  // Video-only chrome (tools/video end card and corner mark).
  'landing.video.brand': 'BuzzBit X',
  'landing.video.cta': 'Get started free',
  'landing.video.tagline': 'Every comment, DM and message — into a customer.',

  // ── a. Instagram comment → DM → gift ─────────────────────────────────────
  'landing.scene.ig.postCaption': 'Comment PLAYBOOK and we will DM you our free automation playbook',
  'landing.scene.ig.postLikes': '2,184 likes',
  'landing.scene.ig.commentAuthor': 'maya.studio',
  'landing.scene.ig.comment': 'PLAYBOOK 🙌',
  'landing.scene.ig.publicReply': 'Sent it to your DMs, Maya!',
  'landing.scene.ig.dm1': 'Hey Maya! Your free Automation Playbook is ready. Quick check first — are you following us?',
  'landing.scene.ig.btnFollowing': 'I’m following ✓',
  'landing.scene.ig.followVerified': 'Follow verified',
  'landing.scene.ig.dm2': 'You’re in! What kind of business do you run?',
  'landing.scene.ig.btnShop': 'Online shop',
  'landing.scene.ig.btnServices': 'Services',
  'landing.scene.ig.btnCoaching': 'Coaching',
  'landing.scene.ig.cardTitle': 'Automation Playbook — Services',
  'landing.scene.ig.cardBody': '4 ready-made automations · 4 min read',
  'landing.scene.ig.cardCta': 'Open your playbook',

  // ── b. WhatsApp booking ──────────────────────────────────────────────────
  'landing.scene.book.hi': 'Hi, can I book a call?',
  'landing.scene.book.ask': 'Of course! What would you like to talk about?',
  'landing.scene.book.btnConsult': 'Consultation',
  'landing.scene.book.btnFollowUp': 'Follow-up',
  'landing.scene.book.btnOther': 'Something else',
  'landing.scene.book.slots': 'Here are the next free slots — shown in your local time:',
  'landing.scene.book.slot1': 'Tue 10:00',
  'landing.scene.book.slot2': 'Tue 14:30',
  'landing.scene.book.slot3': 'Wed 09:15',
  'landing.scene.book.booked': 'Booked · added to the calendar',
  'landing.scene.book.cardTitle': 'You’re booked ✅',
  'landing.scene.book.cardBody': 'Tue 14:30 · 30-minute video call',
  'landing.scene.book.cardCta': 'Join with BuzzCall',
  'landing.scene.book.later': '24 hours before the call',
  'landing.scene.book.reminder': 'Reminder: your call is tomorrow at 14:30. Reply 1 to confirm or 2 to reschedule.',
  'landing.scene.book.confirm': '1',
  'landing.scene.book.thanks': 'Confirmed — see you tomorrow!',

  // ── c. WhatsApp support ──────────────────────────────────────────────────
  'landing.scene.support.q': 'Hi! I have a question about my order',
  'landing.scene.support.menu': 'Happy to help! Pick a topic:',
  'landing.scene.support.btnShipping': 'Shipping times',
  'landing.scene.support.btnReturns': 'Returns',
  'landing.scene.support.btnHuman': 'Talk to a human',
  'landing.scene.support.answer': 'You can return any item within 30 days. Everything you need is here:',
  'landing.scene.support.cardTitle': 'Returns & exchanges',
  'landing.scene.support.cardBody': 'Step-by-step, with a prepaid label',
  'landing.scene.support.cardCta': 'Open the guide',
  'landing.scene.support.more': 'Thanks! Can I talk to someone about an exchange?',
  'landing.scene.support.handoff': 'Handed off to your team inbox',
  'landing.scene.support.human': 'Hi, it’s Dana from the team — I can sort that exchange for you now.',

  // ── Messenger (channel tab) ──────────────────────────────────────────────
  'landing.scene.msg.q': 'Is Thursday’s workshop still open?',
  'landing.scene.msg.a': 'Yes — 4 seats left for Thursday at 18:00. Want one?',
  'landing.scene.msg.btnSave': 'Save my seat',
  'landing.scene.msg.btnDetails': 'Details',
  'landing.scene.msg.cardTitle': 'Seat reserved 🎉',
  'landing.scene.msg.cardBody': 'Thursday 18:00 · confirmation sent by email',
  'landing.scene.msg.cardCta': 'Add to calendar',

  // ── d. Email flow ────────────────────────────────────────────────────────
  'landing.scene.email.trigger': 'New signup',
  'landing.scene.email.triggerMeta': 'from your popup',
  'landing.scene.email.welcome': 'Welcome email',
  'landing.scene.email.welcomeMeta': 'Sent instantly',
  'landing.scene.email.wait': 'Wait 3 days',
  'landing.scene.email.waitMeta': 'Delay',
  'landing.scene.email.tips': 'Day-3 tips email',
  'landing.scene.email.tipsMeta': 'Sent on day 3',
  'landing.scene.email.opened': 'Opened',
  'landing.scene.email.clicked': 'Clicked',
  'landing.scene.email.subject': 'Welcome to Glow Studio ✨',

  // ── e. Reach / analytics ─────────────────────────────────────────────────
  'landing.scene.stats.title': 'Last 30 days',
  'landing.scene.stats.reach': 'Reach',
  'landing.scene.stats.comments': 'Comments',
  'landing.scene.stats.dms': 'DMs sent',
  'landing.scene.stats.bookings': 'Bookings',
  'landing.scene.stats.revenue': 'Revenue',
} as const;
